// Quick-create popover — shown after a drag-create on the grid instead of
// the full NewBookingModal. Lets the user name the event inline; "More
// options" promotes to the full modal. Saving commits the event directly.
//
// Position is computed at open time and CLAMPED to the viewport (so it
// never falls off the bottom or right edge).

import { eventUtcMs, formatTzTime, currentUserIsAdmin, freshBooking } from '../../functions/calendar/calendarUtils.js'

// Mirrors the EVENT_TYPES list in NewBookingModal so the slot overlay offers
// the same picker without importing across files.
const QC_EVENT_TYPES = [
  { type: 'meeting', label: 'Meeting', color: 'green' },
  { type: 'general', label: 'Company event', color: 'blue' },
  { type: 'busy', label: 'Unavailable', color: 'red' }
]
const _qcIsAdmin = currentUserIsAdmin

const POPOVER_W = 380
const POPOVER_H = 300
const MARGIN = 8

export const clampToViewport = (x, y) => {
  const vw = (typeof window !== 'undefined') ? window.innerWidth : 1920
  const vh = (typeof window !== 'undefined') ? window.innerHeight : 1080
  let nx = x, ny = y
  if (nx + POPOVER_W > vw) nx = Math.max(MARGIN, vw - POPOVER_W - MARGIN)
  if (nx < MARGIN) nx = MARGIN
  if (ny + POPOVER_H > vh) ny = Math.max(MARGIN, vh - POPOVER_H - MARGIN)
  if (ny < MARGIN) ny = MARGIN
  return { x: nx, y: ny }
}

export const CalQuickCreate = {
  display: (el, s) => s.root.quickCreate ? 'flex' : 'none',
  position: 'fixed',
  zIndex: 1800,
  flow: 'y',
  gap: 'Z',
  padding: 'A',
  width: `${POPOVER_W}px`,
  round: 'Z2',
  theme: 'modal',
  borderWidth: '1px',
  borderColor: 'line',
  borderStyle: 'solid',
  boxShadow: '0 8px 24px var(--color-shadow)',
  top: (el, s) => s.root.quickCreate ? `${s.root.quickCreate.y}px` : '0',
  left: (el, s) => s.root.quickCreate ? `${s.root.quickCreate.x}px` : '0',

  onCreate: (el, s) => {
    el._closeOnOutside = (ev) => {
      if (!el.node) return
      if (!el.node.contains(ev.target)) {
        s.root.update({ quickCreate: null, dragPreview: null })
      }
    }
    setTimeout(() => document.addEventListener('mousedown', el._closeOnOutside), 0)
  },
  onRemove: (el) => {
    if (el._closeOnOutside) document.removeEventListener('mousedown', el._closeOnOutside)
  },

  Header: {
    flow: 'x',
    align: 'center space-between',

    Time: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      fontFamily: 'monospace',
      text: (el, s) => {
        const qc = s.root.quickCreate
        if (!qc) return ''
        return `${qc.date} · ${qc.startTime}–${qc.endTime}`
      }
    },
    CloseBtn: {
      extends: 'IconButton',
      Icon: { icon: 'crossmark' },
      onClick: (e, el, s) => s.root.update({ quickCreate: null, dragPreview: null })
    }
  },

  TitleField: {
    extends: 'InputField',
    Title: { text: 'Title' },
    Input: {
      tag: 'input',
      type: 'text',
      placeholder: 'Add a title',
      autofocus: true,
      value: (el, s) => s.root.quickCreate?.title || '',
      onInput: (e, el, s) => {
        s.root.update({ quickCreate: { ...s.root.quickCreate, title: e.target.value } })
      },
      onKeydown: (e, el, s) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          _saveQuickCreate(s.root, el)
        }
      }
    }
  },

  // Event-type picker — same row of pills as the full modal but bound to
  // the quickCreate state slice.
  TypePicker: {
    flow: 'x',
    gap: 'Y',
    flexWrap: 'wrap',
    align: 'center left',
    childExtends: 'CalQuickTypeBtn',
    childrenAs: 'state',
    children: (el, s) => {
      const admin = _qcIsAdmin(s.root)
      return QC_EVENT_TYPES.filter(t => admin || !t.adminOnly)
    }
  },

  // Per-invitee local time strip — same data as the modal's pills, but
  // only the first 3 fit here.
  Invitees: {
    display: (el, s) => {
      const qc = s.root.quickCreate
      return (qc?.memberEmails?.length || 0) > 0 ? 'flex' : 'none'
    },
    flow: 'x',
    gap: 'Y',
    flexWrap: 'wrap',

    childExtends: 'CalQuickInvitee',
    childrenAs: 'state',
    children: (el, s) => {
      const qc = s.root.quickCreate
      if (!qc?.memberEmails) return []
      const set = new Set(qc.memberEmails)
      return (s.root.people || []).filter(p => set.has(p.email)).slice(0, 4)
    }
  },

  Footer: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',

    MoreBtn: {
      tag: 'button',
      text: 'More options',
      padding: 'Y Z',
      round: 'Z',
      theme: 'secondary',
      borderWidth: '1px',
      borderColor: 'line',
      borderStyle: 'solid',
      cursor: 'pointer',
      fontSize: 'Y2',
      onClick: (e, el, s) => {
        // Promote to the full booking modal with the quick-create payload.
        const qc = s.root.quickCreate
        if (!qc) return
        s.root.update({
          quickCreate: null,
          dragPreview: null,
          showNewBooking: true,
          newBooking: freshBooking({
            title: qc.title || '',
            date: qc.date,
            startTime: qc.startTime,
            endTime: qc.endTime,
            invitees: (qc.memberEmails || []).join(', '),
            type: qc.type || 'meeting'
          })
        })
      }
    },
    SaveBtn: {
      tag: 'button',
      text: 'Save',
      padding: 'Y A',
      round: 'Z',
      theme: 'primary',
      border: 'none',
      cursor: 'pointer',
      fontSize: 'Z2',
      fontWeight: '600',
      onClick: (e, el, s) => _saveQuickCreate(s.root, el)
    }
  }
}

const _saveQuickCreate = (root, el) => {
  const qc = root.quickCreate
  if (!qc) return
  const ev = {
    id: Date.now(),
    title: qc.title || 'Untitled',
    date: qc.date,
    startTime: qc.startTime,
    endTime: qc.endTime,
    type: qc.type || 'meeting',
    invitees: qc.memberEmails || [],
    description: '',
    creatorEmail: root.currentUserEmail
  }
  root.update({
    events: [...(root.events || []), ev],
    quickCreate: null,
    dragPreview: null
  })
  // Persist to Supabase + trigger Google sync. Patch the optimistic id with
  // the real autoincrement id once the upsert resolves.
  if (el) {
    el.call('saveEventToSupabase', ev).then((realId) => {
      if (!realId) return
      root.update({
        events: (root.events || []).map(e =>
          e.id === ev.id ? { ...e, id: realId } : e
        )
      })
    })
  }
}

// Compact event-type pill for the quick-create overlay. Active state mirrors
// CalEventTypeBtn (filled background, white text) so the two surfaces feel
// like the same control.
export const CalQuickTypeBtn = {
  tag: 'button',
  flow: 'x',
  align: 'center center',
  gap: 'Y',
  padding: 'Y Z',
  round: 'C',
  borderWidth: '1px',
  borderStyle: 'solid',
  cursor: 'pointer',
  fontSize: 'Y2',
  fontWeight: '500',
  height: 'B1',
  minHeight: 'B1',
  borderColor: (el, s) => {
    const cur = el.state.root.quickCreate?.type || 'meeting'
    return cur === s.type ? 'transparent' : 'line'
  },
  background: (el, s) => {
    const cur = el.state.root.quickCreate?.type || 'meeting'
    return cur === s.type ? 'line' : 'transparent'
  },
  color: (el, s) => {
    const cur = el.state.root.quickCreate?.type || 'meeting'
    return cur === s.type ? s.color : 'title'
  },
  ':hover': { opacity: '.9' },
  onClick: (e, el, s) => {
    e.preventDefault()
    el.state.root.update({
      quickCreate: { ...el.state.root.quickCreate, type: s.type }
    })
  },
  Dot: {
    width: 'Y2',
    height: 'Y2',
    round: '50%',
    background: (el, s) => s.color
  },
  Label: { tag: 'span', text: (el, s) => s.label }
}

export const CalQuickInvitee = {
  flow: 'x',
  align: 'center left',
  gap: 'Y',
  padding: 'Y Z',
  round: 'C',
  background: 'overlay',

  Avatar: {
    avatarType: 'initials',
    boxSize: 'Z2 Z2',
    src: (el, s) => el.call('avatarUrl', s.name)
  },
  Name: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'title',
    text: (el, s) => (s.name || '').split(' ')[0]
  },
  LocalTime: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    fontFamily: 'monospace',
    text: (el, s) => {
      const qc = el.state.root.quickCreate
      if (!qc || !s.timezone) return ''
      const sMs = eventUtcMs(qc.date, qc.startTime || '00:00')
      return formatTzTime(sMs, s.timezone)
    }
  }
}
